import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiBell, FiCheck, FiClock, FiExternalLink, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { acknowledgeCrmTaskReminder, fetchCrmDueTaskReminders } from "../crmApi";
import "./CrmTaskReminderPopup.css";

const POLL_INTERVAL_MS = 60000;

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getTaskTypeLabel(value) {
  return String(value || "follow_up")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getContactLabel(contact) {
  if (!contact) return "";
  const fullName = contact.full_name || [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  return fullName || contact.email || contact.phone || "";
}

function getRelatedLabel(task) {
  if (task?.lead?.title) return task.lead.title;
  if (task?.company?.name) return task.company.name;
  if (task?.conversation?.subject) return task.conversation.subject;
  return getContactLabel(task?.contact);
}

export default function CrmTaskReminderPopup({ disabled = false }) {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [processingTaskId, setProcessingTaskId] = useState(null);
  const [error, setError] = useState("");

  const visibleReminders = useMemo(
    () => reminders.filter((task) => !dismissedIds.includes(Number(task.id))),
    [dismissedIds, reminders]
  );
  const current = visibleReminders[0] || null;
  const queueCount = Math.max(visibleReminders.length - 1, 0);
  const relatedLabel = getRelatedLabel(current);

  const refreshReminders = useCallback(async () => {
    const token = localStorage.getItem("palantir_token");
    if (disabled || !token) {
      setReminders([]);
      return;
    }

    try {
      const data = await fetchCrmDueTaskReminders();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setReminders(rows);
      setError("");
    } catch (err) {
      const status = Number(err?.response?.status || 0);
      if (status === 401 || status === 403 || status === 404) {
        setReminders([]);
        setError("");
        return;
      }
      console.warn("[CRM] Could not load task reminders:", err);
      setError("Could not load CRM reminders.");
    }
  }, [disabled]);

  useEffect(() => {
    if (disabled) {
      setReminders([]);
      return undefined;
    }

    refreshReminders();
    const intervalId = window.setInterval(refreshReminders, POLL_INTERVAL_MS);
    window.addEventListener("focus", refreshReminders);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshReminders);
    };
  }, [disabled, refreshReminders]);

  const removeReminder = useCallback((taskId) => {
    setReminders((currentRows) => currentRows.filter((task) => Number(task.id) !== Number(taskId)));
  }, []);

  const acknowledgeReminder = useCallback(
    async (task, { openTask = false } = {}) => {
      if (!task?.id) return;
      setProcessingTaskId(task.id);
      setError("");

      try {
        await acknowledgeCrmTaskReminder(task.id);
        removeReminder(task.id);
        if (openTask) {
          navigate(`/crm/tasks?task_id=${task.id}`);
        }
      } catch (err) {
        console.warn("[CRM] Could not acknowledge task reminder:", err);
        setError("Could not acknowledge this reminder.");
        if (openTask) {
          navigate(`/crm/tasks?task_id=${task.id}`);
        }
      } finally {
        setProcessingTaskId(null);
      }
    },
    [navigate, removeReminder]
  );

  const dismissForSession = useCallback((task) => {
    if (!task?.id) return;
    setDismissedIds((currentIds) => {
      const taskId = Number(task.id);
      return currentIds.includes(taskId) ? currentIds : [...currentIds, taskId];
    });
  }, []);

  if (!current) return null;

  const isProcessing = Number(processingTaskId) === Number(current.id);

  return (
    <aside className="crmTaskReminderPopup" aria-live="polite" aria-label="CRM task reminder">
      <button
        type="button"
        className="crmTaskReminderPopup__close"
        onClick={() => dismissForSession(current)}
        aria-label="Remind me later"
      >
        <FiX aria-hidden="true" />
      </button>

      <button
        type="button"
        className="crmTaskReminderPopup__main"
        onClick={() => acknowledgeReminder(current, { openTask: true })}
        disabled={isProcessing}
      >
        <span className="crmTaskReminderPopup__icon">
          <FiBell aria-hidden="true" />
        </span>
        <span className="crmTaskReminderPopup__content">
          <span className="crmTaskReminderPopup__eyebrow">
            <FiClock aria-hidden="true" />
            Reminder due {formatDateTime(current.reminder_at)}
          </span>
          <strong>{current.title}</strong>
          {relatedLabel ? <em>{relatedLabel}</em> : null}
          <span className="crmTaskReminderPopup__meta">
            {getTaskTypeLabel(current.task_type)} · Due {formatDateTime(current.due_at)}
            {queueCount ? ` · ${queueCount} more` : ""}
          </span>
        </span>
      </button>

      {error ? <div className="crmTaskReminderPopup__error">{error}</div> : null}

      <div className="crmTaskReminderPopup__actions">
        <button type="button" onClick={() => acknowledgeReminder(current)} disabled={isProcessing}>
          <FiCheck aria-hidden="true" />
          <span>{isProcessing ? "Saving" : "Acknowledge"}</span>
        </button>
        <button type="button" onClick={() => acknowledgeReminder(current, { openTask: true })} disabled={isProcessing}>
          <FiExternalLink aria-hidden="true" />
          <span>Open task</span>
        </button>
      </div>
    </aside>
  );
}
